'use client'

import { useEffect, useRef, useState } from 'react'

type TocItem = { id: string; label: string }

/**
 * Sticky table of contents with scroll-spy. The only client-side piece of the
 * Almanac city page -- the rest is server-rendered. Anchor links work without
 * JS; this layers on: (1) highlighting the section in view, (2) keeping the
 * active pill scrolled into view on the mobile horizontal rail, and (3) fading
 * only the rail edge(s) that actually have more pills to scroll toward.
 */
export function AlmanacToc({ items }: { items: TocItem[] }) {
  const [active, setActive] = useState(items[0]?.id ?? '')
  const [edge, setEdge] = useState<'none' | 'start' | 'mid' | 'end'>('none')
  const navRef = useRef<HTMLElement>(null)

  // Scroll-spy: highlight the section currently in view.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const els = items
      .map((t) => document.getElementById(t.id))
      .filter((el): el is HTMLElement => el !== null)
    if (els.length === 0) return

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(e.target.id)
        }
      },
      { rootMargin: '-30% 0px -60% 0px' }
    )
    els.forEach((el) => obs.observe(el))
    return () => obs.disconnect()
  }, [items])

  // Mobile horizontal rail: track which edges have more content so the CSS mask
  // fades only the side(s) you can actually scroll toward (and never the ends).
  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const update = () => {
      const max = nav.scrollWidth - nav.clientWidth
      if (max <= 1) return setEdge('none')
      const x = nav.scrollLeft
      setEdge(x <= 1 ? 'start' : x >= max - 1 ? 'end' : 'mid')
    }
    update()
    nav.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      nav.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [items])

  // Keep the active pill in view on the horizontal rail as you read down the page.
  // Scroll the rail itself (horizontal only) — NOT scrollIntoView, which also acts
  // on the block axis and, on page load (sticky rail below the hero), scrolled the
  // whole window down to the TOC, defeating the route's scroll-to-top and dumping
  // mobile users mid-page.
  useEffect(() => {
    const nav = navRef.current
    const el = nav?.querySelector<HTMLElement>('.is-active')
    if (!nav || !el) return
    const navRect = nav.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()
    const delta = elRect.left + elRect.width / 2 - (navRect.left + navRect.width / 2)
    nav.scrollBy({ left: delta, behavior: 'smooth' })
  }, [active])

  return (
    <aside className="alm-toc" aria-label="On this page">
      <p className="alm-toc__title">On this page</p>
      <nav ref={navRef} data-edge={edge}>
        {items.map((t) => (
          <a key={t.id} href={`#${t.id}`} className={t.id === active ? 'is-active' : undefined}>
            {t.label}
          </a>
        ))}
      </nav>
    </aside>
  )
}
